import useTrackFilterVendors from '@hooks/useTrackFilterVendors';
import type { FilterConfig } from '@pages/VendorsSearch/containers/FilterPills/atoms';
import { useUpdateFilterValues } from '@pages/VendorsSearch/containers/FilterPills/hooks/use-update-filter-values';
import type { FacetSingleSelectFilterStrategy } from '@pages/VendorsSearch/containers/FilterPills/strategies/facet-single-select-filter-strategy';
import { useAppSelector } from '@redux/hooks';
import React, { type FunctionComponent, useMemo } from 'react';
import PillWithDropdown from '../../../../../components/shared/PillWithDropdown';
import { labels, subtext } from '../../utils';

export interface FacetSingleSelectFilterProps {
	filterConfig: FilterConfig;
	appliedFilter?: FacetSingleSelectFilterStrategy;
}

export const DEFAULT_ALL_RADIO_OPTION_ID = 'DEFAULT_ALL_RADIO_OPTION_ID';
export const defaultOption = {
	name: 'All',
	value: DEFAULT_ALL_RADIO_OPTION_ID,
};

export const FacetSingleSelectFilter: FunctionComponent<
	FacetSingleSelectFilterProps
> = ({ filterConfig, appliedFilter }) => {
	const { updateFilterValues, removeFilterValue } = useUpdateFilterValues();
	const displayMarketCode = useAppSelector(
		(state) => state.location.marketCode,
	);
	const isMobile = useAppSelector((state) => state.viewport.isMobile);
	const [trackVendors] = useTrackFilterVendors({ displayMarketCode });
	const selectedOptions = useMemo(() => {
		const facetId = appliedFilter?.getData().value.facetId;
		return facetId ? [facetId] : [];
	}, [appliedFilter]);

	const options = useMemo(() => {
		const filterOptions =
			filterConfig?.filterOptions.map((o) => ({
				name: o.name,
				value: o.id,
				subtext: subtext[o.id as keyof typeof subtext],
			})) || [];
		return filterConfig?.hideAllOption
			? filterOptions
			: [defaultOption, ...filterOptions];
	}, [filterConfig?.filterOptions, filterConfig?.hideAllOption]);

	const onSave = (filtersToApply: string[]) => {
		const facetId = filtersToApply[0];
		const selectedOption = filterConfig?.filterOptions.find(
			(option) => option.id === facetId,
		);
		if (!selectedOption) {
			removeFilterValue(filterConfig.id);
		} else {
			updateFilterValues([{ id: filterConfig.id, value: { facetId } }]);
		}
		trackVendors('save selected filters');
	};

	const onClear = () => {
		removeFilterValue(filterConfig.id);
		trackVendors('clear selected filters');
	};

	const pillLabel =
		labels[filterConfig.name as keyof typeof labels] || filterConfig.name;

	return (
		<PillWithDropdown
			isMobile={isMobile}
			onClearOptions={onClear}
			onSave={onSave}
			pillLabel={pillLabel}
			appliedOptions={selectedOptions}
			options={options}
			singleSelect={true}
			defaultOptions={
				filterConfig?.hideAllOption ? undefined : [DEFAULT_ALL_RADIO_OPTION_ID]
			}
		/>
	);
};
